import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button, Spinner } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { GetChatFlow, UpdateChatFlow } from '../../../Services/HeedifyServices';
import { getAccessToken } from '../../../Services/GraphServices';
import NodesPalette from './NodesPalette';
import EditNodePanel from './EditNodePanel';
import CustomEdge from './CustomEdge';
import { ConfirmAlert } from '../../GenericComponent/ConfirmAlert';
import './CSS/ChatFlowDiagram.css';
import './CSS/CustomEdge.css';

// Import des utilitaires et hooks
import { useNodeGeneration } from './hooks/useNodeGeneration';
import { 
  createEdgeHandlers,
  createNodeHandlers,
  createDragDropHandlers,
  createConnectionHandler
} from './utils/eventHandlers';

/**
 * ChatFlowDiagram - Visualisation du ChatFlow avec ReactFlow
 * Architecture modulaire avec séparation des responsabilités
 */
const ChatFlowDiagramInner = ({ st_ChatFlows, st_CallFlows, ACTIVE_TENANT, Token, SCOPES }) => {
  const { t } = useTranslation();
  const { chatFlowId } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;

  // États principaux
  const [loading, setLoading] = useState(true);
  const [chatFlowData, setChatFlowData] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const reactFlowWrapper = useRef(null);

  // États pour EditNodePanel
  const [editingNode, setEditingNode] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // État pour la confirmation de suppression
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    type: null,
    item: null,
    name: ''
  });

  // Handler pour sauvegarder dans la base de données
  const saveChatFlowToDatabase = useCallback((updatedChatFlowData) => {
    return getAccessToken(instance, activeAccount)
      .then((token) => {
        const scope = updatedChatFlowData.scope || 'default';
        return UpdateChatFlow(
          ACTIVE_TENANT,
          userId,
          updatedChatFlowData.id,
          scope,
          token,
          updatedChatFlowData
        );
      });
  }, [instance, activeAccount, ACTIVE_TENANT, userId]);

  // Handler pour sauvegarder les modifications de nœud
  const handleSaveNode = useCallback(async (updatedChatFlowData) => {
    try {
      const token = Token || (await getAccessToken(instance, activeAccount));
      const scope = updatedChatFlowData.scope || 'default';
      
      await UpdateChatFlow(
        ACTIVE_TENANT,
        userId,
        updatedChatFlowData.id,
        scope,
        token,
        updatedChatFlowData
      );
      
      setChatFlowData(updatedChatFlowData);
      handleClosePanel();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
    }
  }, [Token, instance, activeAccount, ACTIVE_TENANT, userId]);

  // Handlers pour EditNodePanel
  const handleEditNode = useCallback((nodeId) => {
    setEditingNode({ id: nodeId });
    setIsPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
    setEditingNode(null);
  }, []);

  // Créer les handlers d'événements
  const edgeHandlers = createEdgeHandlers({
    chatFlowData,
    setChatFlowData,
    setEdges,
    setNodes,
    saveChatFlowToDatabase,
    instance,
    activeAccount,
    ACTIVE_TENANT,
    userId,
    chatFlowId
  });

  const nodeHandlers = createNodeHandlers({
    chatFlowData,
    handleSaveNode
  });

  const dragDropHandlers = createDragDropHandlers({
    chatFlowData,
    Token,
    instance,
    activeAccount,
    ACTIVE_TENANT,
    userId,
    setChatFlowData
  });

  const connectionHandler = createConnectionHandler({
    chatFlowData,
    setChatFlowData,
    saveChatFlowToDatabase
  });

  // Handlers pour les dialogues de confirmation
  const handleEdgeDelete = useCallback((edgeId) => {
    setDeleteConfirmation({
      open: true,
      type: 'edge',
      item: edgeId,
      name: 'cette liaison'
    });
  }, []);

  const handleNodeDelete = useCallback((nodeId) => {
    if (nodeId === 'routing-0') {
      console.warn('⚠️ La suppression du routing "0" est interdite');
      return;
    }
    
    setDeleteConfirmation({
      open: true,
      type: 'node',
      item: nodeId,
      name: nodeId === 'greeting' ? 'Greeting' : nodeId.replace('routing-', 'Routing ')
    });
  }, []);

  const confirmDelete = useCallback(() => {
    if (deleteConfirmation.type === 'node') {
      if (deleteConfirmation.item === 'greeting') {
        const updatedChatFlowData = {
          ...chatFlowData,
          greeting: {}
        };
        handleSaveNode(updatedChatFlowData);
      } else if (deleteConfirmation.item.startsWith('routing-')) {
        const routingKey = deleteConfirmation.item.replace('routing-', '');
        const updatedRouting = { ...chatFlowData.routing };
        delete updatedRouting[routingKey];
        
        // Nettoyer les références
        Object.keys(updatedRouting).forEach(key => {
          const routing = updatedRouting[key];
          
          if (routing.type === 'Customer Choice' || routing.type === 'customerChoice') {
            if (routing.customerChoiceActions) {
              const updatedActions = {};
              Object.keys(routing.customerChoiceActions).forEach(actionKey => {
                const action = routing.customerChoiceActions[actionKey];
                if (action.next?.type === 'routing' && action.next.routingName === routingKey) {
                  updatedActions[actionKey] = {
                    ...action,
                    next: undefined
                  };
                } else {
                  updatedActions[actionKey] = action;
                }
              });
              updatedRouting[key] = {
                ...routing,
                customerChoiceActions: updatedActions
              };
            }
          }
          
          if (routing.type === 'Form' || routing.type === 'form') {
            if (routing.next?.type === 'routing' && routing.next.routingName === routingKey) {
              updatedRouting[key] = {
                ...routing,
                next: undefined
              };
            }
          }
        });
        
        const updatedChatFlowData = {
          ...chatFlowData,
          routing: updatedRouting
        };
        
        handleSaveNode(updatedChatFlowData);
      }
    } else if (deleteConfirmation.type === 'edge') {
      edgeHandlers.handleEdgeDeleteInternal(deleteConfirmation.item);
    }
    setDeleteConfirmation({ open: false, type: null, item: null, name: '' });
  }, [deleteConfirmation, edgeHandlers, chatFlowData, handleSaveNode]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirmation({ open: false, type: null, item: null, name: '' });
  }, []);

  // Chargement des données ChatFlow
  useEffect(() => {
    const loadChatFlow = async () => {
      setLoading(true);
      try {
        const existingFlow = st_ChatFlows?.find((f) => f.id === chatFlowId);
        
        if (existingFlow) {
          setChatFlowData(existingFlow);
        } else {
          const token = Token || (await getAccessToken(instance, activeAccount));
          const flowScope = 'default';
          const response = await GetChatFlow(ACTIVE_TENANT, userId, chatFlowId, flowScope, token);
          
          if (response) {
            if (!response.routing || !response.routing["0"]) {
              response.routing = {
                "0": {
                  enabled: true,
                  type: "queue",
                  displayName: "Start Routing",
                  order: 0
                },
                ...response.routing
              };
            }
            setChatFlowData(response);
          }
        }
      } catch (error) {
        console.error('Erreur chargement ChatFlow:', error);
      } finally {
        setLoading(false);
      }
    };

    if (chatFlowId && chatFlowId !== 'newChatFlow') {
      loadChatFlow();
    } else {
      setLoading(false);
    }
  }, [chatFlowId, st_ChatFlows, ACTIVE_TENANT, Token, instance, activeAccount, userId]);

  // Utiliser le hook pour générer les nœuds
  useNodeGeneration({
    chatFlowData,
    setNodes,
    setEdges,
    handleEditNode,
    handleDeleteNode: handleNodeDelete,
    handleEdgeDelete,
    handleEdgeLabelChange: edgeHandlers.handleEdgeLabelChange,
    reactFlowInstance
  });

  // Navigation au clic sur un nœud
  const onNodeClick = (event, node) => {
    handleEditNode(node.id);
  };

  if (loading) {
    return (
      <div className="diagram-loading">
        <Spinner label={t('LOADING_DIAGRAM')} />
      </div>
    );
  }

  if (!chatFlowData) {
    return (
      <div className="diagram-error">
        <div>❌ {t('CHAT_FLOW_NOT_FOUND')}</div>
        <Button onClick={() => navigate('/chatFlows')}>{t('BACK')}</Button>
      </div>
    );
  }

  return (
    <div className="chatflow-diagram-container">
      {/* Left Panel - NodesPalette */}
      <NodesPalette chatFlowData={chatFlowData} />

      {/* Center - ReactFlow */}
      <div 
        className="diagram-flow-area" 
        ref={reactFlowWrapper}
        onDrop={dragDropHandlers.onDrop}
        onDragOver={dragDropHandlers.onDragOver}
      >
        <div className="diagram-callflow-name">{chatFlowData?.displayName || 'ChatFlow'}</div>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          edgeTypes={{ custom: CustomEdge }}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={connectionHandler.onConnect}
          isValidConnection={connectionHandler.isValidConnection}
          onNodeClick={onNodeClick}
          onInit={setReactFlowInstance}
          connectionMode="loose"
          fitView
          minZoom={0.5}
          maxZoom={1.5}
        >
          <Controls />
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        </ReactFlow>
      </div>

      {/* Right Panel - EditNodePanel */}
      <EditNodePanel
        node={editingNode}
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        onSave={handleSaveNode}
        chatFlowData={chatFlowData}
        setChatFlowData={setChatFlowData}
        st_CallFlows={st_CallFlows}
        ACTIVE_TENANT={ACTIVE_TENANT}
        SCOPES={SCOPES}
        onDeleteNode={nodeHandlers.handleDeleteNode}
      />

      {/* Confirmation Dialog */}
      <ConfirmAlert
        open={deleteConfirmation.open}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        name={deleteConfirmation.name}
      />
    </div>
  );
};

// Wrapper avec ReactFlowProvider
const ChatFlowDiagramComponent = (props) => {
  return (
    <ReactFlowProvider>
      <ChatFlowDiagramInner {...props} />
    </ReactFlowProvider>
  );
};

export default ChatFlowDiagramComponent;
 